import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { useAuth, isAdmin } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import { PageHeader, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, FolderPlus } from "lucide-react";
import DataTableBlock, { dataTableListTitle } from "../components/DataTableBlock";
import DataTablePagination from "../components/DataTablePagination";
import { useDataTable } from "../hooks/useDataTable";
import { useClientPagination } from "../hooks/useClientPagination";
import { LOOKUP_OPTION_COLUMNS } from "./lookupOptionColumns";

export default function DropdownChoicesPage() {
  const { user } = useAuth();
  const { reload: reloadAppLookups } = useLookups();
  const canManage = isAdmin(user);

  const [groups, setGroups] = useState([]);
  const [groupKey, setGroupKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState({ value: "", label: "" });

  // New group dialog state
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ key: "", name: "", description: "" });
  const [savingGroup, setSavingGroup] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = canManage ? { include_inactive: 1 } : {};
      const { data } = await api.get("/lookup-options", { params });
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [user, canManage]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-select first group when groups load and nothing selected yet
  useEffect(() => {
    if (!groupKey && groups.length) {
      setGroupKey(groups[0].key);
    }
  }, [groups, groupKey]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.key === groupKey),
    [groups, groupKey],
  );

  const activeOptions = useMemo(
    () => (selectedGroup?.options || []).filter((o) => o.is_active !== false),
    [selectedGroup],
  );

  const inactiveOptions = useMemo(
    () => (selectedGroup?.options || []).filter((o) => o.is_active === false),
    [selectedGroup],
  );
  const activeTable = useDataTable(activeOptions, LOOKUP_OPTION_COLUMNS, { id: "label", desc: false });
  const inactiveTable = useDataTable(inactiveOptions, LOOKUP_OPTION_COLUMNS, { id: "label", desc: false });
  const activePagination = useClientPagination(activeTable.rows);
  const inactivePagination = useClientPagination(inactiveTable.rows);

  const addOption = async (e) => {
    e.preventDefault();
    const value = addForm.value.trim();
    const label = (addForm.label || value).trim();
    if (!value) { toast.error("Value is required"); return; }
    try {
      await api.post("/lookup-options", { group_key: groupKey, value, label });
      toast.success("Choice added");
      setAddForm({ value: "", label: "" });
      await load();
      await reloadAppLookups();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const removeOption = async (row) => {
    if (!window.confirm(`Remove "${row.label || row.value}" from this dropdown?`)) return;
    try {
      await api.delete(`/lookup-options/${row.id}`);
      toast.success("Choice removed");
      await load();
      await reloadAppLookups();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const restoreOption = async (row) => {
    try {
      await api.patch(`/lookup-options/${row.id}`, { is_active: true });
      toast.success("Choice restored");
      await load();
      await reloadAppLookups();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    setSavingGroup(true);
    try {
      const { data } = await api.post("/lookup-groups", groupForm);
      toast.success(`Group "${data.name}" created`);
      setNewGroupOpen(false);
      setGroupForm({ key: "", name: "", description: "" });
      await load();
      await reloadAppLookups();
      setGroupKey(data.key);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingGroup(false);
    }
  };

  // Auto-fill key from name
  const handleGroupNameChange = (name) => {
    const autoKey = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    setGroupForm((f) => ({
      ...f,
      name,
      key: f.key && f.key !== autoKey.slice(0, f.key.length + 1) ? f.key : autoKey,
    }));
  };

  if (user && !isAdmin(user)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dropdown-choices-page">
      <PageHeader
        eyebrow="Settings"
        title="Dropdown choices"
        description="Manage options for all dropdown fields across the app. Each group corresponds to a dropdown. Add new groups for custom fields or extend existing system groups with more choices."
        actions={
          canManage ? (
            <Button
              type="button"
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={() => setNewGroupOpen(true)}
              data-testid="new-group-button"
            >
              <FolderPlus className="h-4 w-4 mr-1" /> New Group
            </Button>
          ) : null
        }
      />

      {!canManage && (
        <SectionCard title="View only">
          <p className="text-sm text-muted-foreground">
            You can view current choices. An administrator with "Manage dropdown choices" permission can add or remove options.
          </p>
        </SectionCard>
      )}

      {/* Group selector */}
      <SectionCard title="Select dropdown">
        <div className="max-w-md">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Category</Label>
          <Select value={groupKey} onValueChange={setGroupKey}>
            <SelectTrigger className="mt-1.5" data-testid="lookup-group-select">
              <SelectValue placeholder="Select group…" />
            </SelectTrigger>
            <SelectContent>
              {groups.map((g) => (
                <SelectItem key={g.key} value={g.key}>
                  <span>{g.name}</span>
                  {!g.is_system && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">custom</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedGroup?.description && (
            <p className="text-xs text-muted-foreground mt-2">{selectedGroup.description}</p>
          )}
          {selectedGroup && (
            <p className="text-[10px] font-mono text-muted-foreground mt-1 opacity-70">
              key: {selectedGroup.key}
            </p>
          )}
        </div>
      </SectionCard>

      {/* Active choices */}
      <SectionCard title={dataTableListTitle("active choices", activeOptions.length, { filtered: activeTable.rows.length, hasFilters: activeTable.hasActiveFilters })}>
        <DataTableBlock
          title=""
          wrapInSectionCard={false}
          columns={LOOKUP_OPTION_COLUMNS}
          table={activeTable}
          testId="lookup-active-filters"
          showBody={false}
        />
        {loading ? (
          <p className="text-sm text-muted-foreground mt-4">Loading…</p>
        ) : (
          <>
          <ul className="divide-y rounded-md border mt-4">
            {activePagination.rows.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{o.label || o.value}</span>
                  {o.label && o.label !== o.value && (
                    <span className="text-muted-foreground font-mono text-xs ml-2">{o.value}</span>
                  )}
                </div>
                {canManage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => removeOption(o)}
                    data-testid={`lookup-remove-${o.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
            {activePagination.rows.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                {activeTable.hasActiveFilters ? "No choices match your search." : "No active choices in this group. Add one below."}
              </li>
            )}
          </ul>
          <DataTablePagination
            meta={activePagination.meta}
            onPageChange={activePagination.setPage}
            testId="lookup-active-pagination"
          />
          </>
        )}
      </SectionCard>

      {/* Removed / inactive */}
      {inactiveOptions.length > 0 && (
        <SectionCard title={dataTableListTitle("inactive choices", inactiveOptions.length, { filtered: inactiveTable.rows.length, hasFilters: inactiveTable.hasActiveFilters })}>
          <DataTableBlock
            title=""
            wrapInSectionCard={false}
            columns={LOOKUP_OPTION_COLUMNS}
            table={inactiveTable}
            testId="lookup-inactive-filters"
            showBody={false}
          />
          <>
          <ul className="divide-y rounded-md border mt-4">
            {inactivePagination.rows.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm opacity-60">
                <div>
                  <span>{o.label || o.value}</span>
                  {o.label && o.label !== o.value && (
                    <span className="text-muted-foreground font-mono text-xs ml-2">{o.value}</span>
                  )}
                </div>
                {canManage && (
                  <Button type="button" variant="outline" size="sm" onClick={() => restoreOption(o)}>
                    Restore
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <DataTablePagination
            meta={inactivePagination.meta}
            onPageChange={inactivePagination.setPage}
            testId="lookup-inactive-pagination"
          />
          </>
        </SectionCard>
      )}

      {/* Add choice form */}
      {canManage && groupKey && (
        <SectionCard title="Add a choice">
          <form onSubmit={addOption} className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Value <span className="normal-case not-italic">(stored in DB)</span>
              </Label>
              <Input
                className="mt-1.5 font-mono"
                value={addForm.value}
                onChange={(e) => setAddForm((f) => ({ ...f, value: e.target.value }))}
                placeholder="e.g. 1718 or SAME_DAY"
                required
                data-testid="lookup-add-value"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Display label <span className="normal-case not-italic">(shown in forms)</span>
              </Label>
              <Input
                className="mt-1.5"
                value={addForm.label}
                onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="Optional — defaults to value"
                data-testid="lookup-add-label"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </form>
          <p className="text-xs text-muted-foreground mt-3">
            For system fields (bardana, payment, etc.) use stable uppercase codes like <code className="font-mono text-xs">SAME_DAY</code>.
            For varieties / custom groups, the value itself is usually the display name.
          </p>
        </SectionCard>
      )}

      {/* New group dialog */}
      <Dialog open={newGroupOpen} onOpenChange={setNewGroupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create new dropdown group</DialogTitle>
            <DialogDescription>
              A group represents a dropdown field. Once created, you can add choices to it from this page.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createGroup} className="space-y-4 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input
                className="mt-1.5"
                value={groupForm.name}
                onChange={(e) => handleGroupNameChange(e.target.value)}
                placeholder="e.g. Moisture level"
                required
                data-testid="new-group-name"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Key <span className="normal-case not-italic">(auto-filled, must be unique)</span>
              </Label>
              <Input
                className="mt-1.5 font-mono"
                value={groupForm.key}
                onChange={(e) =>
                  setGroupForm((f) => ({
                    ...f,
                    key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                  }))
                }
                placeholder="moisture_level"
                required
                data-testid="new-group-key"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Lowercase letters, digits, underscores only. Cannot be changed after creation.
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description (optional)</Label>
              <Input
                className="mt-1.5"
                value={groupForm.description}
                onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Where is this dropdown used?"
                data-testid="new-group-description"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0 pt-2">
              <Button type="button" variant="outline" onClick={() => setNewGroupOpen(false)} disabled={savingGroup}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                disabled={savingGroup}
                data-testid="new-group-submit"
              >
                {savingGroup ? "Creating…" : "Create group"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
